import { IssueTaskDoc, OrderDoc, OrderStatusCode, issueTaskCategoryMap } from '@gooduncles/gu-app-schema';
import { StoreIssueDoc } from '@gooduncles/gu-app-schema';
import { Dictionary } from 'lodash';
import { groupBy, uniq } from 'lodash-es';
import { OrderUI } from 'src/schema/schema-order-ui';
import { formatDate } from 'src/utils/date-util';

import { shouldIncludeInAmountCalculation } from './issue-task-util';
import { calcTaxForPrice } from './order-util';
import { jsonToExcelFile } from './xlsx-util';

type OrderWithIssues = Partial<OrderDoc> & {
  orderDate: string;
  _ui: {
    storeIssues: StoreIssueDoc[];
    issueTasks: IssueTaskDoc[];
  };
};

const groupingRule = (order: OrderDoc) => {
  return formatDate(order.date ?? order.orderDate, 'yy년 M월');
};

/**
 * 세율이 10%인 경우 가격에서 세금을 계산한다.
 */
export const calculatePreTaxPrice = (priceWithTax: number, taxFree: boolean) => {
  if (taxFree) {
    return {
      price: priceWithTax,
      tax: 0,
    };
  }
  // 세전 가격 = 세후 가격 / 1.1
  const preTaxPrice = Math.round(priceWithTax / 1.1);
  const tax = priceWithTax - preTaxPrice;

  return {
    price: preTaxPrice,
    tax,
  };
};

/**
 * 별도의 이슈 컬렉션을 운영하고 있는 과도기에만 사용한다.
 * @param orders
 * @param storeIssues
 * @returns 주문, 주문+이슈, 이슈 세가지의 형태로 된 배열을 리턴한다.
 */
export const getHistoryItems = (
  orders: OrderDoc[],
  storeIssues: StoreIssueDoc[] | undefined,
  issueTasks: IssueTaskDoc[] | undefined
): Dictionary<((OrderDoc & OrderUI) | OrderWithIssues)[]> => {
  const noneDeliveryOrders = orders.filter((order) => order.orderStatus !== OrderStatusCode.DELIVERED);
  /**
   * 이슈 데이터는 배송 완료된 주문과만 연결된다.
   */
  const deliveredOrders = orders.filter(
    (order) => order.orderStatus === OrderStatusCode.DELIVERED && (order.date ?? order.deliveryDate)
  );

  /**
   * 앱과 별개로 운영되고 있는 이슈 데이터를 주문과 엮는 명확한 규칙은 존재하지 않는다.
   * 때문에 임시로 배송일과 이슈 적용일을 비교 가능한 형태('L월 d일 (EEEEEE)')로 변환한다.
   */
  const groupedByDate = groupBy(storeIssues, (issue) => formatDate(issue.date, 'L월 d일 (EEEEEE)'));
  const groupedByDeliveredDate = groupBy(deliveredOrders, (order) =>
    formatDate(order.date ?? order.deliveryDate, 'L월 d일 (EEEEEE)')
  );
  const validIssueTasks =
    issueTasks?.filter((task): task is IssueTaskDoc & { targetDate: string } => !!task.targetDate) ?? [];
  const groupedByIssueTaskDate = groupBy(validIssueTasks, (task) => formatDate(task.targetDate, 'L월 d일 (EEEEEE)'));

  /**
   * 이슈와 주문이 같은 날짜인 경우 묶어주고 그렇지 않은 경우에는 별도로 표현해야한다.
   */
  const deliveryDates = uniq([
    ...Object.keys(groupedByDeliveredDate),
    ...Object.keys(groupedByDate),
    ...Object.keys(groupedByIssueTaskDate),
  ]);
  /**
   * 현재 함수의 목적은 주문과 이슈를 묶어서 표현하는 것이다.
   * 주문에 묶을 수 없는, 즉 주문이 없고 이슈만 있는 경우가 존재하기 때문에 이를 위한 처리가 필요하다.
   * 한 화면에 같이 보여주기 때문에 서로 다른 형태의 데이터에 동일한 정렬을 적용해야한다.
   * 이 때문에 기존 정렬 기준이 되는 orderDate에 이슈 date를 임의로 넣어준다.
   */
  const orderWithIssues: OrderWithIssues[] = deliveryDates.map((date) => {
    // 주문이 배열 형태이나, 이는 배열을 그룹핑한 결과이기 때문에 현재는(정책상 주문을 하루에 하나만 생성하므로) length가 1인 경우만 존재한다.
    const order = groupedByDeliveredDate[date];
    if (order) {
      return {
        ...order[0],
        _ui: {
          storeIssues: groupedByDate[date],
          issueTasks: groupedByIssueTaskDate[date],
        },
      };
    }
    // 주문이 없는 경우: storeIssue 또는 issueTask 중 하나라도 있어야 함
    const storeIssueDate = groupedByDate[date]?.[0]?.date;
    const issueTaskDate = groupedByIssueTaskDate[date]?.[0]?.targetDate;
    return {
      orderDate: (storeIssueDate || issueTaskDate) ?? '',
      _ui: {
        storeIssues: groupedByDate[date],
        issueTasks: groupedByIssueTaskDate[date],
      },
    };
  });

  // 주문일자를 기준으로 정렬한다.(최근 날짜가 위로 오도록 정렬)
  const groupedList = groupBy(
    [...noneDeliveryOrders, ...orderWithIssues].sort(
      (a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime()
    ),
    groupingRule
  );
  return groupedList as Dictionary<(OrderDoc | OrderWithIssues)[]>;
};

/**
 * 주문 내역 엑셀 다운로드(only for web)
 * @param date 'yy년 M월'
 */
export const historyExcelDownload = (date: string, items: (OrderDoc | OrderWithIssues)[]) => {
  const fileName = `주문내역_${date}`;
  const heading = [['일자', '내용', '수량', '과/면세', '공급가', '세액', '총금액']];

  const rows = items.map((item) => {
    const deliveredOrder = item.orderStatus === OrderStatusCode.DELIVERED ? (item as OrderDoc) : undefined;
    // 주문 상품
    const orderProducts = deliveredOrder ? deliveredOrder.products : undefined;
    // 배송비
    const deliveryFee = deliveredOrder ? deliveredOrder.deliveryFee : undefined;
    // 이슈
    const storeIssues = (item as OrderDoc & OrderUI)._ui?.storeIssues;
    const issueTasks = (item as OrderDoc & OrderUI)._ui?.issueTasks;
    const deliveryDate = item.date ?? item.deliveryDate;

    const productRows =
      orderProducts?.map((product) => {
        const { price, tax } = calculatePreTaxPrice(product.snapshotPrice ?? product.price, product.taxFree);

        return {
          date: deliveryDate,
          text: product.fullName,
          volume: product.volume,
          taxFree: product.taxFree ? '면세' : '과세',
          price,
          tax: tax * product.volume,
          total: (price + tax) * product.volume,
        };
      }) ?? [];

    const deliveryFeeRow = deliveryFee
      ? (() => {
          const { price: deliveryPrice, tax: deliveryTax } = calculatePreTaxPrice(deliveryFee, false);
          return [
            {
              date: deliveryDate,
              text: '배송비',
              volume: 1,
              taxFree: '과세',
              price: deliveryPrice,
              tax: deliveryTax,
              total: deliveryFee,
            },
          ];
        })()
      : [];

    const storeIssueRows =
      storeIssues?.map((issue) => {
        const price = issue.supplyPrice ?? 0;
        const tax = issue.tax ?? 0;
        const volume = issue.volume ?? 1;
        const total = price ? (price + tax) * volume : null;
        return {
          date: issue.date ? formatDate(issue.date, 'yyyy-MM-dd') : '',
          text: issue.message,
          volume,
          taxFree: tax !== 0 ? '과세' : '면세',
          price,
          tax,
          total,
        };
      }) ?? [];

    const issueTaskRows =
      issueTasks?.flatMap((task) => {
        const shouldIncludeAmount = shouldIncludeInAmountCalculation(task);
        const taskProducts = task.products;
        if (taskProducts && taskProducts.length > 0) {
          return taskProducts.map((product) => {
            const { volume, tax, totalPrice } = calcTaxForPrice(product.price, product.quantity, product.taxFree);
            return {
              date: task.targetDate ? formatDate(task.targetDate, 'yyyy-MM-dd') : '',
              text: `${issueTaskCategoryMap[task.category]}${task.publicNote ? `(${task.publicNote})` : ''} - ${
                product.productName
              }`,
              volume,
              taxFree: product.taxFree ? '면세' : '과세',
              price: shouldIncludeAmount ? product.price : 0,
              tax: shouldIncludeAmount ? tax : 0,
              total: shouldIncludeAmount ? totalPrice : 0,
            };
          });
        }
        // products가 없으면 amount 정보로 단일 행 생성
        const supplyPrice = task.amount?.supplyAmount ?? 0;
        const tax = task.amount?.taxAmount ?? 0;
        const total = task.amount?.totalAmount ?? 0;
        return [
          {
            date: task.targetDate ? formatDate(task.targetDate, 'yyyy-MM-dd') : '',
            text: `${issueTaskCategoryMap[task.category]}${task.publicNote ? `(${task.publicNote})` : ''}`,
            volume: 1,
            taxFree: tax !== 0 ? '과세' : '면세',
            price: shouldIncludeAmount ? supplyPrice : 0,
            tax: shouldIncludeAmount ? tax : 0,
            total: shouldIncludeAmount ? total : 0,
          },
        ];
      }) ?? [];

    return [...productRows, ...deliveryFeeRow, ...storeIssueRows, ...issueTaskRows];
  });

  jsonToExcelFile(rows.flat(), fileName, heading);
};
